import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { api } from "../lib/api";
import { toast } from "sonner";
import { CheckCircle2, CalendarDays, Clock, Mail } from "lucide-react";

function nextDays(n) {
  return Array.from({ length: n }, (_, i) => {
    const d = new Date();
    d.setDate(d.getDate() + i);
    return {
      value: d.toISOString().slice(0, 10),
      label: d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" }),
    };
  });
}

export const BookingDialog = ({ court, open, onOpenChange, preset }) => {
  const baseDays = nextDays(7);
  const days = preset && !baseDays.some((d) => d.value === preset.date)
    ? [{ value: preset.date, label: new Date(preset.date + "T00:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" }) }, ...baseDays]
    : baseDays;
  const [date, setDate] = useState(days[0].value);
  const [slots, setSlots] = useState([]);
  const [time, setTime] = useState(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [confirmation, setConfirmation] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open && preset) {
      setDate(preset.date);
      setTime(preset.time);
    }
  }, [open, preset]);

  useEffect(() => {
    if (!court || !open) return;
    api.get(`/courts/${court.id}/slots`, { params: { date } }).then((r) => {
      setSlots(r.data);
      setTime((t) => {
        if (!t) return null;
        const fresh = r.data.find((s) => s.time === t);
        if (fresh && fresh.booked) {
          toast.error("That slot was just taken — pick another time");
          return null;
        }
        return fresh ? t : null;
      });
    }).catch(() => setSlots([]));
  }, [court, date, open]);

  useEffect(() => {
    if (!open) setConfirmation(null);
  }, [open]);

  const book = async (e) => {
    e.preventDefault();
    if (!time) return toast.error("Pick a time slot first");
    setSubmitting(true);
    try {
      const r = await api.post("/bookings", { court_id: court.id, date, time, name, email });
      setConfirmation(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Booking failed — try another slot");
    } finally {
      setSubmitting(false);
    }
  };

  if (!court) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0f0f10] border-white/15 text-white max-w-lg max-h-[90vh] [max-height:90dvh] overflow-y-auto" data-testid="booking-dialog">
        {confirmation ? (
          <div className="text-center py-6" data-testid="booking-confirmation">
            <CheckCircle2 className="mx-auto text-[#ccff00] mb-4" size={56} />
            <h3 className="font-display uppercase text-3xl tracking-tight mb-2">Court Booked</h3>
            <p className="text-white/60 mb-6">Demo booking — no payment taken.</p>
            <div className="bg-[#1a1a1a] border border-[#ccff00]/30 rounded-lg p-6 text-left space-y-2">
              <p className="text-[#ccff00] font-mono text-lg" data-testid="booking-ref">{confirmation.ref}</p>
              <p><span className="text-white/50">Court:</span> {confirmation.court_name}</p>
              <p><span className="text-white/50">When:</span> {confirmation.date} at {confirmation.time}</p>
              <p><span className="text-white/50">Player:</span> {confirmation.name}</p>
              <p><span className="text-white/50">Rate:</span> ${confirmation.price}/hr (simulated)</p>
            </div>
            <div className="mt-4 bg-[#141414] border border-white/10 rounded-lg p-5 text-left" data-testid="booking-email-preview">
              <div className="flex items-center gap-2 mb-3">
                <Mail size={15} className="text-[#ccff00]" />
                <p className="text-xs uppercase tracking-widest text-white/50">Confirmation email — simulated</p>
              </div>
              <p className="text-sm text-white/60"><span className="text-white/40">To:</span> {confirmation.email}</p>
              <p className="text-sm text-white/60 mt-1"><span className="text-white/40">Subject:</span> Your court is booked — {confirmation.ref}</p>
              <p className="text-sm text-white/75 mt-3 leading-relaxed border-t border-white/10 pt-3">
                Hi {confirmation.name}, your booking at The Point Strategy Hub is confirmed: {confirmation.court_name} on {confirmation.date} at {confirmation.time}. Keep reference <span className="text-[#ccff00] font-mono">{confirmation.ref}</span> to view or cancel under "My Booking". See you on court!
              </p>
              <p className="text-[11px] text-white/35 mt-3">Demo site — no real email was sent.</p>
            </div>
            <p className="text-white/50 text-sm mt-4">
              Save your reference — view or cancel it anytime under <span className="text-[#ccff00]">My Booking</span>.
            </p>
            <button
              onClick={() => onOpenChange(false)}
              data-testid="booking-done-btn"
              className="mt-6 bg-[#ccff00] text-black font-bold uppercase tracking-wider px-8 py-3 rounded-full hover:bg-[#a3e635] transition-colors"
            >
              Done
            </button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-display uppercase text-2xl tracking-tight">
                Book <span className="text-[#ccff00]">{court.name}</span>
              </DialogTitle>
              <DialogDescription className="text-white/50">Pick a date and time — demo booking, no payment.</DialogDescription>
            </DialogHeader>
            <form onSubmit={book} className="space-y-6 mt-2">
              <div>
                <p className="text-xs uppercase tracking-widest text-white/50 mb-3 flex items-center gap-2"><CalendarDays size={14} /> Date</p>
                <div className="flex gap-2 flex-wrap">
                  {days.map((d) => (
                    <button
                      type="button"
                      key={d.value}
                      onClick={() => setDate(d.value)}
                      data-testid={`booking-date-${d.value}`}
                      className={`px-3 py-2 rounded-md text-xs border transition-colors ${
                        date === d.value
                          ? "bg-[#ccff00] text-black border-[#ccff00] font-bold"
                          : "border-white/15 text-white/70 hover:border-[#ccff00]/50"
                      }`}
                    >
                      {d.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-xs uppercase tracking-widest text-white/50 mb-3 flex items-center gap-2"><Clock size={14} /> Time slot</p>
                <div className="grid grid-cols-4 gap-2">
                  {slots.map((s) => (
                    <button
                      type="button"
                      key={s.time}
                      disabled={s.booked}
                      onClick={() => setTime(s.time)}
                      data-testid={`booking-slot-${s.time}`}
                      className={`py-2 rounded-md text-xs border transition-colors ${
                        s.booked
                          ? "border-white/5 text-white/20 line-through cursor-not-allowed"
                          : time === s.time
                            ? "bg-[#ccff00] text-black border-[#ccff00] font-bold"
                            : "border-white/15 text-white/70 hover:border-[#ccff00]/50"
                      }`}
                    >
                      {s.time}
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <input
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name"
                  data-testid="booking-name-input"
                  aria-label="Your name"
                  className="bg-[#1a1a1a] border border-white/15 rounded-md px-4 py-3 text-sm focus:outline-none focus:border-[#ccff00]/60 placeholder:text-white/35"
                />
                <input
                  required
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email"
                  data-testid="booking-email-input"
                  aria-label="Email"
                  className="bg-[#1a1a1a] border border-white/15 rounded-md px-4 py-3 text-sm focus:outline-none focus:border-[#ccff00]/60 placeholder:text-white/35"
                />
              </div>
              <button
                type="submit"
                disabled={submitting}
                data-testid="booking-submit-btn"
                className="w-full bg-[#ccff00] text-black font-bold uppercase tracking-wider py-3.5 rounded-full hover:bg-[#a3e635] transition-colors disabled:opacity-50"
              >
                {submitting ? "Booking…" : `Confirm — $${court.price}/hr (demo)`}
              </button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default BookingDialog;
